import React from 'react'

import { Flex } from '@theme-ui/components'
import { Trans, useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'
import { Container, Animation } from 'rsuite'
import styled from 'styled-components'

import { baseColors, Spinner } from '@fairhq/common'
import { questionSessionActions } from 'store/questionSession/questionSessionSlice'
import { State } from 'store/state'
import { Subtitle } from 'styled'
import { Message } from 'styled/message'
import { tCode } from 'utils/tCode'

import { useAppSelector, useAppDispatch } from '../../store/hooks'

import { QuestionTypeCard } from './QuestionTypeCard'

const QuestionCardContainer = styled.div<{ type?: string }>`
  max-width: 800px;
  margin: 0 auto 20px;
  align-items: center;
  margin-bottom: 124px;
  flex: 1;
  display: flex;
  flex-flow: column;
  justify-content: center;

  ${Subtitle} {
    margin-bottom: 8px;
    text-align: center;
    justify-content: center;
  }

  ${Message} {
    text-align: center;

    &[type='indication'] {
      margin-top: 18px;
      margin-bottom: 0;
    }
  }
`

const QuestionCardHeaderContainer = styled.div<{ type?: string }>`
  background-color: ${baseColors.backgroundsBackground};
  z-index: 1;
  width: 100%;
  text-align: center;
  padding-bottom: 30px;

  p {
    text-align: center;
    justify-content: center;
  }
`

const QuestionTypeCardContainer = styled(Container)`
  width: 100%;
  flex: initial;

  .rs-radio-group-picker .rs-radio-checker > label,
  .rs-radio-group-picker .rs-radio-checker > label:hover,
  .rs-radio-group-picker .rs-radio-checker > label:focus {
    color: ${({ theme }) => theme.colors.grey500};
  }
`

export const QuestionCard = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const {
    questionIndex,
    question,
    numberValue,
    selectedOption,
    selectedOptions,
    other,
    skipped,
  } = useAppSelector(
    (state: State) => ({
      question: state.questionSessionReducer.question,
      questionIndex: state.questionSessionReducer.currentIndex + 1,
      numberValue: state.questionSessionReducer.value,
      selectedOption: state.questionSessionReducer.selectedOption,
      selectedOptions: state.questionSessionReducer.selectedOptions ?? [],
      other: state.questionSessionReducer.other,
      skipped: state.questionSessionReducer.assessment?.skipped,
    }),
    shallowEqual
  )
  const { title, code, description } = question ?? {}
  const tQuestion = tCode(t)
  const { type } = question ?? {}

  if (!type) {
    return <Spinner center content={t('loading')} />
  }

  return (
    <Animation.Slide in placement="bottom">
      {(props, ref) => (
        <QuestionCardContainer
          type={type}
          ref={ref}
          {...props}
          key={question?.code}
        >
          <QuestionCardHeaderContainer type={type}>
            {skipped ? (
              <Flex
                as="p"
                sx={{
                  color: baseColors.contentContent,
                  backgroundColor: baseColors.boldActionInfo,
                  border: '1px solid',
                  borderColor: baseColors.borderDefault,
                  padding: '8px 16px',
                  borderRadius: '30px',
                  display: 'inline-flex',
                  marginTop: '36px',
                  marginBottom: '24px',
                  strong: { marginRight: '5px' },
                }}
              >
                <Trans i18nKey="question.skipped" components={[<strong />]} />
              </Flex>
            ) : undefined}
            <Subtitle>
              {t('question.number', { number: questionIndex })}
            </Subtitle>
            <Message type="question">{tQuestion(code, 'title', title)}</Message>
            {description ? (
              <Message type="indication">
                {tQuestion(code, 'description', description)}
              </Message>
            ) : undefined}
          </QuestionCardHeaderContainer>
          {question && (
            <QuestionTypeCardContainer>
              <QuestionTypeCard
                question={question}
                onNumberChange={(newValue: string) => {
                  dispatch(questionSessionActions.setValue(newValue))
                }}
                numberValue={numberValue}
                selectedOption={selectedOption}
                setSelectedOption={(value: any) => {
                  dispatch(questionSessionActions.setSelectedOption(value))
                }}
                selectedOptions={selectedOptions}
                setSelectedOptions={(values: any) => {
                  dispatch(questionSessionActions.setSelectedOptions(values))
                }}
                other={other}
                setOther={(value: string) =>
                  dispatch(questionSessionActions.setOther(value))
                }
              />
            </QuestionTypeCardContainer>
          )}
        </QuestionCardContainer>
      )}
    </Animation.Slide>
  )
}
